import {ColumnDef, FilterDef, FilterOperator, FilterType, IdModelTable} from "../../../../components/ExTable";
import {useTranslation} from "react-i18next";
import {
    Termination,
    TerminationField,
    TerminationItem,
    TerminationItemField,
    TerminationItemReturnStatus,
    TerminationItemTestResult,
    TerminationStatus,
} from "../../../../app/api/model/termination";
import {DateFormat, formatTimestamp} from "../../../../utils/datetime";
import {getTranslation, TranslationContext} from "../../../../utils/getTranslationKey";
import {TerminationItemEnumSelect} from "./TerminationItemEnumSelect";
import {useTerminationItemListQuery} from "../../../../app/api/api";
import {EMPTY_UI} from "../../../../variables";

export interface TerminationItemsTableProps {
    termination: Termination;
}

export function TerminationItemsTable({termination}: Readonly<TerminationItemsTableProps>) {
    const {t} = useTranslation();
    const isReadonly = [TerminationStatus.DONE, TerminationStatus.WITHDRAWN].includes(
        termination[TerminationField.STATUS]
    );
    const columnDefs: ColumnDef<TerminationItem>[] = [
        {
            fieldName: TerminationItemField.ID,
            align: "left",
            label: t("generic_id"),
            width: 50,
            sortable: true,
            sticky: true,
        },
        {
            fieldName: TerminationItemField.HARDWARE_TYPE,
            align: "left",
            label: t("generic_type"),
            width: 75,
            sortable: true,
        },
        {
            fieldName: TerminationItemField.LEO_ID,
            align: "left",
            label: t("generic_leoId"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: TerminationItemField.SERIAL_NUMBER,
            align: "left",
            label: t("generic_serialNumber"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: TerminationItemField.SHORT_ICCID,
            align: "left",
            label: t("generic_shortIccid"),
            width: 100,
            sortable: true,
        },
        {
            fieldName: TerminationItemField.RETURN_DATE,
            align: "left",
            label: t("terminationItem_returnDate"),
            width: 100,
            sortable: true,
            formatCellText: ({cellValue, row}) => {
                return row[TerminationItemField.RETURN_STATUS] !== TerminationItemReturnStatus.OBSOLETE
                    ? formatTimestamp(cellValue, DateFormat.DATE)
                    : EMPTY_UI;
            },
        },
        {
            fieldName: TerminationItemField.PREDECESSOR_LEO_ID,
            align: "left",
            label: t("generic_predecessorLeoId"),
            width: 200,
            sortable: false,
        },
        {
            fieldName: TerminationItemField.RETURN_STATUS,
            align: "left",
            label: t("terminationItem_returnStatus"),
            width: 200,
            sortable: true,
            formatCellText: ({row}) => {
                return row[TerminationItemField.RETURN_STATUS] !== TerminationItemReturnStatus.OBSOLETE
                    ? {
                          title: getTranslation({
                              context: TranslationContext.TERMINATION_ITEM_RETURN_STATUS,
                              value: row[TerminationItemField.RETURN_STATUS],
                              t,
                          }),
                          node: (
                              <TerminationItemEnumSelect
                                  terminationId={termination[TerminationField.ID]}
                                  item={row}
                                  field={TerminationItemField.RETURN_STATUS}
                                  options={Object.values(TerminationItemReturnStatus).filter(
                                      (rs) => rs !== TerminationItemReturnStatus.OBSOLETE
                                  )}
                                  context={TranslationContext.TERMINATION_ITEM_RETURN_STATUS}
                                  isReadonly={isReadonly}
                              />
                          ),
                      }
                    : EMPTY_UI;
            },
        },
        {
            fieldName: TerminationItemField.TESTED_AT,
            align: "left",
            label: t("terminationItem_testedAt"),
            width: 100,
            sortable: true,
            formatCellText: ({cellValue, row}) => {
                return row[TerminationItemField.TEST_RESULT] !== TerminationItemTestResult.OBSOLETE
                    ? formatTimestamp(cellValue, DateFormat.DATE)
                    : EMPTY_UI;
            },
        },
        {
            fieldName: TerminationItemField.TEST_RESULT,
            align: "left",
            label: t("terminationItem_testResult"),
            width: 200,
            sortable: true,
            formatCellText: ({row}) => {
                return row[TerminationItemField.TEST_RESULT] !== TerminationItemTestResult.OBSOLETE
                    ? {
                          title: getTranslation({
                              context: TranslationContext.TERMINATION_ITEM_TEST_RESULT,
                              value: row[TerminationItemField.TEST_RESULT],
                              t,
                          }),
                          node: (
                              <TerminationItemEnumSelect
                                  terminationId={termination[TerminationField.ID]}
                                  item={row}
                                  field={TerminationItemField.TEST_RESULT}
                                  options={Object.values(TerminationItemTestResult).filter(
                                      (tr) => tr !== TerminationItemTestResult.OBSOLETE
                                  )}
                                  context={TranslationContext.TERMINATION_ITEM_TEST_RESULT}
                                  isReadonly={isReadonly}
                              />
                          ),
                      }
                    : EMPTY_UI;
            },
        },
        {
            fieldName: TerminationItemField.CANCELLATION_REASON,
            align: "left",
            label: t("terminationItem_cancellationReason"),
            width: 250,
            sortable: false,
            formatCellText: ({row}) => {
                return row[TerminationItemField.CANCELLATION_REASON]
                    ? t(`terminationCancellationReason_${row[TerminationItemField.CANCELLATION_REASON]}`)
                    : EMPTY_UI;
            },
        },
    ];
    const filterDefs: FilterDef<TerminationItem>[] = [
        {
            fieldName: TerminationItemField.ID,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: TerminationItemField.HARDWARE_TYPE,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: TerminationItemField.LEO_ID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: TerminationItemField.SERIAL_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: TerminationItemField.SHORT_ICCID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: TerminationItemField.RETURN_DATE,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: TerminationItemField.RETURN_STATUS,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: Object.values(TerminationItemReturnStatus).map((rs) => ({
                value: rs,
                labelKey:
                    rs !== TerminationItemReturnStatus.OBSOLETE
                        ? getTranslation({
                              context: TranslationContext.TERMINATION_ITEM_RETURN_STATUS,
                              value: rs,
                              t,
                          })
                        : EMPTY_UI,
            })),
            type: FilterType.ENUM,
        },
        {
            fieldName: TerminationItemField.TESTED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: TerminationItemField.TEST_RESULT,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: Object.values(TerminationItemTestResult).map((tr) => ({
                value: tr,
                labelKey:
                    tr !== TerminationItemTestResult.OBSOLETE
                        ? getTranslation({
                              context: TranslationContext.TERMINATION_ITEM_TEST_RESULT,
                              value: tr,
                              t,
                          })
                        : EMPTY_UI,
            })),
            type: FilterType.ENUM,
        },
    ];
    return (
        <IdModelTable
            queryHook={useTerminationItemListQuery}
            additionalRequestParams={{id: termination[TerminationField.ID], params: {}}}
            columnDefs={columnDefs}
            filterDefs={filterDefs}
            title={t("termination_items")}
            selectableRows={false}
        />
    );
}
